import React from 'react';
import BaseWidget from './BaseWidget';
import {
  commonSerializedKeys,
  type SerializedVote,
  type SerializedVotingUserData,
  type SerializedVotingSessionWidget,
} from '.';
import VotingSessionWidgetComponent from '../components/widgets/VotingSessionWidgetComponent';
import { randomId } from '@aha-app/mvc';
import type { RecordId } from '../types';
import type { KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { timeInSeconds } from '../utils/time';
import { VotingSessionType } from './VotingSessionType';

export enum VotingSessionStatus {
  Inactive,
  Active,
  Paused,
}

const DEFAULT_ATTRIBUTES = {
  startedAt: null,
  status: VotingSessionStatus.Inactive,
  exactDuration: null,
};

class VotingSessionWidget
  extends BaseWidget
  implements SerializedVotingSessionWidget
{
  type: 'VotingSessionWidget' = 'VotingSessionWidget';

  name: string;
  status: VotingSessionStatus;
  startedAt: number | null;
  exactDuration: number | null;
  votableObjects?: { [votableId: RecordId]: boolean };
  currentVotingObject: {
    recordId: RecordId;
    votingStatus: 'started' | 'finished';
  } | null;

  previousVotingObject: RecordId | undefined;

  userData?: {
    [userId: RecordId]: SerializedVotingUserData;
  };

  sessionType: VotingSessionType;
  votes?: {
    [authorId: RecordId]: { [voteId: RecordId]: SerializedVote | null };
  };

  votesPerPerson?: number;
  pokerChoices?: string[];
  pokerFinalScores?: { [recordId: string]: string };

  serializedKeys: [
    ...typeof commonSerializedKeys,
    ...(keyof SerializedVotingSessionWidget)[]
  ] = [
    ...commonSerializedKeys,
    'name',
    'status',
    'startedAt',
    'exactDuration',
    'votableObjects',
    'userData',
    'sessionType',
    'votes',
    'votesPerPerson',
    'pokerChoices',
    'pokerFinalScores',
    'currentVotingObject',
    'previousVotingObject',
  ];

  constructor(attributes: Partial<SerializedVotingSessionWidget> = {}) {
    super({ ...DEFAULT_ATTRIBUTES, ...attributes });
    this.eventTarget = new EventTarget();
    this.tickTimeout = null;
    this.startTickTimer();

    this.sessionType ??= VotingSessionType.Standard;

    this.cache = {};
  }

  get isActive(): boolean {
    return (
      this.status === VotingSessionStatus.Active && this.remainingDuration > 0
    );
  }

  get isFinished(): boolean {
    return this.remainingDuration <= 0;
  }

  // The remaining duration of the voting session in seconds
  get remainingDuration(): number {
    if (this.status !== VotingSessionStatus.Active) {
      return -1;
    }

    const duration = this.totalDuration;

    if (!this.startedAt) {
      return timeInSeconds(duration);
    }

    const elapsed = Date.now() - this.startedAt;
    return Math.max(timeInSeconds(duration - elapsed), 0);
  }

  get totalDuration(): number {
    return this.exactDuration ?? Infinity;
  }

  get inWarningTime(): boolean {
    return (
      !this.inDangerTime &&
      this.remainingDuration <= timeInSeconds(this.totalDuration) * 0.25
    );
  }

  get inDangerTime(): boolean {
    return this.remainingDuration <= 20;
  }

  addEventListener(
    type: string,
    listener: (event: Event) => void,
    options?: boolean | AddEventListenerOptions
  ) {
    this.eventTarget.addEventListener(type, listener, options);
  }

  removeEventListener(
    type: string,
    listener: (event: Event) => void,
    options?: boolean | EventListenerOptions
  ) {
    this.eventTarget.removeEventListener(type, listener, options);
  }

  private handleTick(): void {
    if (this.isActive && this.exactDuration) {
      this.eventTarget.dispatchEvent(new Event('tick'));
    } else if (this.isFinished) {
      // Dispatch the finish event first, otherwise the tick event can
      // trigger behaviors (like resetting the active widget) that can
      // prevent the finish event handlers from running.
      this.eventTarget.dispatchEvent(new Event('finish'));
      this.eventTarget.dispatchEvent(new Event('tick'));
    }
  }

  private startTickTimer() {
    if (this.tickTimeout !== null) {
      return;
    }

    const handler = () => {
      this.handleTick();

      if (this.isActive && this.exactDuration) {
        this.tickTimeout = setTimeout(handler, 1000);
      } else if (this.isFinished) {
        this.tickTimeout = null;
      }
    };

    handler();
  }

  isVotable(objectId: string): boolean {
    switch (this.sessionType) {
      // In fist-to-five you don't vote on individual objects, but on the whole whiteboard
      case VotingSessionType.FistToFive: {
        return objectId === this.id;
      }
      // in standard voting you can vote on any object, or on a pre-selected subset
      case VotingSessionType.Standard: {
        if (!this.votableObjects) {
          return true;
        }
        return !!this.votableObjects[objectId];
      }
      // in planning poker you can only vote on selected objects
      case VotingSessionType.Poker: {
        if (!this.pokerFinalScores) {
          return false;
        }
        return !!this.pokerFinalScores[objectId];
      }
      default: {
        const _exhaustivenessCheck: never = this.sessionType;
        return _exhaustivenessCheck;
      }
    }
  }

  // The main voting data structure that the other vote count methods
  // are based off of. This is in charge of, for each object, counting
  // the number of votes each user gave to that object. It is also in
  // charge of limiting votes to the vote limit -- if a user reaches
  // their total vote limit, future votes in the data structure by
  // that user will be ignored. So no matter what happens
  // simultaneously, the voting limit should always be respected.
  votesByObjectAndUser(): {
    [objectId: string]: { [userId: RecordId]: number };
  } {
    const votes: { [objectId: string]: { [userId: RecordId]: number } } = {};
    const totalUserVotes: { [userId: RecordId]: number } = {};
    if (!this.votes) {
      return votes;
    }

    if (this.cache.votesByObjectAndUser) {
      return this.cache.votesByObjectAndUser;
    }

    Object.values(this.votes).forEach(userVotes => {
      Object.values(userVotes).forEach(vote => {
        if (vote === null) {
          return;
        }

        if (votes[vote.votableId] === undefined) {
          votes[vote.votableId] = {};
        }

        if (votes[vote.votableId][vote.authorId] === undefined) {
          votes[vote.votableId][vote.authorId] = 0;
        }

        if (totalUserVotes[vote.authorId] === undefined) {
          totalUserVotes[vote.authorId] = 0;
        }

        if (
          !this.votesPerPerson ||
          totalUserVotes[vote.authorId] < this.votesPerPerson
        ) {
          const votesRemaining = this.votesPerPerson
            ? this.votesPerPerson - totalUserVotes[vote.authorId]
            : Infinity;
          const voteValue = Math.min(vote.value ?? 1, votesRemaining);

          if (this.sessionType === VotingSessionType.Poker) {
            votes[vote.votableId][vote.authorId] = voteValue;
          } else {
            votes[vote.votableId][vote.authorId] += voteValue;
          }
          // Does totalUserVotes matter for planning poker?
          totalUserVotes[vote.authorId] += voteValue;
        }
      });
    });

    this.cache.votesByObjectAndUser = votes;
    this.cache.totalUserVotes = totalUserVotes;
    return votes;
  }

  userVotesByObject(userId: RecordId): {
    [objectId: string]: number;
  } {
    const votes = this.votesByObjectAndUser();
    const votesByObject = {};

    Object.entries(votes).forEach(([objectId, userVotes]) => {
      votesByObject[objectId] = userVotes[userId] ?? 0;
    });

    return votesByObject;
  }

  votesByObject(): { [objectId: string]: number } {
    const votes = this.votesByObjectAndUser();
    const votesByObject = {};

    if (this.cache.votesByObject) {
      return this.cache.votesByObject;
    }

    Object.entries(votes).forEach(([objectId, userVotes]) => {
      votesByObject[objectId] = Object.values(userVotes).reduce(
        (sum, voteCount) => sum + voteCount,
        0
      );
    });

    this.cache.votesByObject = votesByObject;
    return votesByObject;
  }

  get totalVotes() {
    return Object.values(this.votesByObject()).reduce(
      (sum, voteCount) => sum + voteCount,
      0
    );
  }

  get totalVotesByUser(): { [userId: RecordId]: number } {
    if (this.cache.totalUserVotes) {
      return this.cache.totalUserVotes;
    }

    // votesByObjectAndUser generates this as part of its work
    this.votesByObjectAndUser();
    return this.cache.totalUserVotes ?? {};
  }

  get totalVoters(): number {
    return this.voters.length;
  }

  get voters(): RecordId[] {
    const allVoters = [
      ...(Object.keys(this.totalVotesByUser ?? {}) as RecordId[]),
      ...(Object.keys(this.userData ?? {}) as RecordId[]),
    ];
    return Array.from(new Set(allVoters));
  }

  votesRemaining(userId: RecordId): number {
    if (this.userFinishedVoting(userId)) return 0;

    return (
      (this.votesPerPerson ?? Infinity) - (this.totalVotesByUser[userId] ?? 0)
    );
  }

  userHasVotedOnObject(id: string, userId: RecordId): boolean {
    // FistToFive can distinguish between a 0 vote and not voted, so
    // we need to do the same. Just check for the presence of a voting
    // count.
    return this.votesByObjectAndUser()[id]?.[userId] !== undefined;
  }

  userFinishedVoting(userId: RecordId): boolean {
    return (
      this.userMarkedAsFinished(userId) ||
      this.totalVotesByUser[userId] >= (this.votesPerPerson ?? Infinity)
    );
  }

  userMarkedAsFinished(userId: RecordId): boolean {
    return this.userData?.[userId]?.finishedVoting ?? false;
  }

  isWinner(objectId: string): boolean {
    const votes = this.votesByObject();
    const counts = Object.values(votes);
    const maxVotes = Math.max(...counts);
    const winningObjectIds = Object.entries(votes).reduce(
      (ids, [id, count]) => (count === maxVotes ? [...ids, id] : ids),
      []
    );

    if (winningObjectIds.length === 1) {
      return winningObjectIds[0] === objectId;
    } else {
      // Only one object can win, no ties here
      return false;
    }
  }

  votesForObject(id: string): number {
    return this.votesByObject()[id] ?? 0;
  }

  userVotesForObject(id: string, userId: RecordId | null): number {
    if (!userId) return 0;

    return this.userVotesByObject(userId)[id] ?? 0;
  }

  canVoteForObject(_id: string, userId: RecordId): boolean {
    if (!this.isActive) {
      return false;
    }

    if (this.userMarkedAsFinished(userId)) {
      return false;
    }

    const userVotes = this.userVotesByObject(userId);

    if (!this.votesPerPerson) {
      return true;
    }

    return (
      Object.values(userVotes).reduce((sum, voteCount) => sum + voteCount, 0) <
      this.votesPerPerson
    );
  }

  canUnvoteForObject(id: string, userId: RecordId): boolean {
    if (!this.isActive) {
      return false;
    }

    if (this.userMarkedAsFinished(userId)) {
      return false;
    }

    const userVotes = this.userVotesForObject(id, userId);
    return userVotes > 0;
  }

  voteForObject(id: string, userId: RecordId, count = 1): void {
    const voteId = randomId() as RecordId;

    const data = {
      id: voteId,
      type: 'Vote',
      votableId: id,
      authorId: userId,
      createdAt: Date.now(),
      value: count,
    };

    for (const [key, value] of Object.entries(data)) {
      this.set(['votes', userId, voteId, key], value);
    }
  }

  unvoteForObject(id: string, userId: RecordId, count = 1): void {
    const votes = this.votes?.[userId];
    if (!votes) {
      return;
    }

    for (const vote of Object.values(votes)) {
      if (vote?.votableId !== id) continue;

      const voteValue = vote.value ?? 1;

      if (count < voteValue) {
        this.set(['votes', userId, vote.id, 'value'], voteValue - count);
      } else {
        this.set(['votes', userId, vote.id], null);
      }

      count -= voteValue;

      if (count <= 0) break;
    }
  }

  setVotesForObject(id: string, userId: RecordId, value: number): void {
    const count = this.userVotesForObject(id, userId);

    this.unvoteForObject(id, userId, count);
    this.voteForObject(
      id,
      userId,
      Math.min(value, this.votesPerPerson ?? Infinity)
    );
  }

  setFinalObjectScore(recordId: string, score: string) {
    this.set(['pokerFinalScores', recordId], score);
  }

  markUserAsFinished(userId: RecordId, finished = true) {
    const path = ['userData', userId, 'finishedVoting'];
    if (finished) {
      this.set(path, true);
    } else {
      this.set(path, null);
    }
  }

  _set(keyOrPath: KeyPath, value: any) {
    if (
      keyOrPath === 'votes' ||
      (Array.isArray(keyOrPath) && keyOrPath[0] === 'votes')
    ) {
      this.cache = {};
    }

    if (keyOrPath === 'status') {
      this.startTickTimer();
    }

    super._set(keyOrPath, value);

    if (keyOrPath === 'exactDuration') {
      this.handleTick();
    }
  }

  render(): JSX.Element | null {
    if (!this.controller.canVote || !this.isActive) {
      return null;
    }

    return <VotingSessionWidgetComponent widget={this} key={this.id} />;
  }
}

export default VotingSessionWidget;
